import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Tooltip,
  Box,
  Typography,
  Grid,
  Paper,
  useTheme
} from '@mui/material';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { moroccanHolidays2026, getHolidayInfo } from '../../utils/moroccanHolidays';

export default function HighlightCalendar({ highlightedDates, open, setOpen }) {
  const theme = useTheme();
  const [currentDate, setCurrentDate] = useState(new Date(2026, 1, 1)); // Start at February 2026

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    return { daysInMonth, startingDayOfWeek };
  };

  const isHighlighted = (day) => {
    return highlightedDates.some(date =>
      date.getDate() === day &&
      date.getMonth() === currentDate.getMonth() &&
      date.getFullYear() === currentDate.getFullYear()
    );
  };

  const isToday = (day) => {
    const today = new Date();
    return (
      day === today.getDate() &&
      currentDate.getMonth() === today.getMonth() &&
      currentDate.getFullYear() === today.getFullYear()
    );
  };

  const isHoliday = (day) => {
    return moroccanHolidays2026.some(holiday =>
      holiday.date.getDate() === day &&
      holiday.date.getMonth() === currentDate.getMonth() &&
      holiday.date.getFullYear() === currentDate.getFullYear()
    );
  };

  const renderCalendar = () => {
    const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentDate);
    const days = [];

    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(<Box key={`empty-${i}`} />);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const highlighted = isHighlighted(day);
      const today = isToday(day);
      const holiday = isHoliday(day);
      const holidayInfo = getHolidayInfo(day, currentDate.getMonth(), currentDate.getFullYear());

      let bgcolor = 'background.paper';
      let color = 'text.primary';
      let hoverBg = 'action.hover';
      let border = 'none';

      if (today) {
        border = `2px solid ${theme.palette.primary.main}`;
        bgcolor = 'background.default';
      }

      if (highlighted) {
        bgcolor = 'primary.main'; // Or linear gradient
        color = 'primary.contrastText';
        hoverBg = 'primary.dark';
      }

      if (holidayInfo?.type === 'islamic') {
        bgcolor = 'success.main'; // Greenish
        color = 'white';
        hoverBg = 'success.dark';
      } else if (holidayInfo?.type === 'national') {
        bgcolor = 'error.main'; // Reddish
        color = 'white';
        hoverBg = 'error.dark';
      }


      const dayElement = (
        <Box
          key={day}
          sx={{
            aspectRatio: '1/1',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: 2,
            fontSize: '0.875rem',
            cursor: 'pointer',
            transition: 'all 0.2s',
            bgcolor: bgcolor,
            color: color,
            border: border,
            position: 'relative',
            '&:hover': {
              transform: 'scale(1.05)',
              bgcolor: hoverBg,
              boxShadow: 2
            }
          }}
        >
          {day}
        </Box>
      );

      days.push(
        holiday ? (
          <Tooltip key={day} title={holidayInfo?.name || 'Holiday'} arrow placement="top">
            {dayElement}
          </Tooltip>
        ) : (
          dayElement
        )
      );
    }

    return days;
  };

  const changeMonth = (offset) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + offset);
    setCurrentDate(newDate);
  };

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      maxWidth="sm"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: 3,
          p: 1
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Typography variant="h6">Calendar</Typography>
        <IconButton onClick={() => setOpen(false)} size="small">
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ maxWidth: 400, mx: 'auto', minHeight: 400 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <IconButton onClick={() => changeMonth(-1)} sx={{ bgcolor: 'action.selected' }}>
              <ChevronLeft size={20} />
            </IconButton>
            <Typography variant="h6" fontWeight="bold">
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </Typography>
            <IconButton onClick={() => changeMonth(1)} sx={{ bgcolor: 'action.selected' }}>
              <ChevronRight size={20} />
            </IconButton>
          </Box>

          {/* Grid */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
            {dayNames.map(day => (
              <Typography key={day} variant="caption" align="center" fontWeight="bold" color="text.secondary" sx={{ py: 1 }}>
                {day}
              </Typography>
            ))}
            {renderCalendar()}
          </Box>

          {/* Legend */}
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3, flexWrap: 'wrap' }}>
            {[
              { label: 'Highlighted', color: 'primary.main' },
              { label: 'Today', color: 'background.default', border: `2px solid ${theme.palette.primary.main}` },
              { label: 'Islamic Holiday', color: 'success.main' },
              { label: 'National Holiday', color: 'error.main' }
            ].map((item, i) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 16, height: 16, borderRadius: 1, bgcolor: item.color, border: item.border || 'none' }} />
                <Typography variant="caption" color="text.secondary">{item.label}</Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
